import type { Espacio } from '@/lib/useEspacioDisponible'

/** Separación entre tarjetas en escritorio (el `gap-4` del sistema). */
export const SEPARACION_GRILLA = 16
const PROPORCION_TARJETA = 4 / 5
/** Las columnas que se pueden elegir en escritorio (DEC-08). */
export const COLUMNAS_POSIBLES = [3, 4, 5, 6] as const
export type Columnas = (typeof COLUMNAS_POSIBLES)[number]
export const COLUMNAS_INICIALES: Columnas = 6
/**
 * Cuánto se puede achicar una tarjeta para que las filas entren enteras en
 * la pantalla. Más que eso, se prefiere la tarjeta grande y una fila cortada.
 */
const ACHIQUE_MAXIMO = 0.15

export interface MedidasGrilla {
  columnas: number
  /** Ancho de cada tarjeta, en píxeles. */
  ancho: number
}

/**
 * Tamaño de las tarjetas para las columnas que eligió la persona (DEC-08,
 * OBS-013: con unas 15 piezas por mes, ocho columnas se veían vacías).
 *
 * Las tarjetas llenan el ancho. Si achicándolas un poco (hasta 15 %) entran
 * dos filas enteras en el alto de la pantalla, se achican hasta ahí: así en
 * 1360×768 seis columnas siguen dando dos filas enteras (DEC-07). Si no,
 * quedan del ancho y la fila de abajo se ve cortada, que es lo que se pide al
 * elegir menos columnas: verlas más grandes. Nunca más alta que la pantalla.
 *
 * `null` si no entran las columnas elegidas: queda la grilla de siempre.
 */
export function medidasGrilla(
  { ancho, alto }: Espacio,
  columnas: number = COLUMNAS_INICIALES,
): MedidasGrilla | null {
  const s = SEPARACION_GRILLA
  const llenando = Math.floor((ancho - s * (columnas - 1)) / columnas)
  if (llenando <= 0) return null
  const anchoParaFilas = (filas: number) =>
    Math.floor(((alto - s * (filas - 1)) / filas) * PROPORCION_TARJETA)

  // Nunca más alta que la pantalla; y si con un achique chico entran dos
  // filas enteras, se achica hasta ahí y no más.
  const unaFila = Math.min(llenando, anchoParaFilas(1))
  const dosFilas = anchoParaFilas(2)
  const elegido =
    dosFilas >= llenando * (1 - ACHIQUE_MAXIMO) ? Math.min(llenando, dosFilas) : unaFila
  return { columnas, ancho: Math.max(1, elegido) }
}

/** Separación entre las piezas de un día del calendario. */
const SEPARACION_PIEZAS = 2
/** Número del día y márgenes de la celda. */
const CABEZA_CELDA = 32
/** Por debajo de este alto de celda, las piezas van en formato compacto. */
export const CELDA_COMPACTA = 110

/**
 * Cuántas piezas de un día entran en su celda. Las demás quedan detrás del
 * "+N", que va en el renglón del número y no les quita lugar.
 */
export function piezasQueEntran(altoCelda: number, total: number): number {
  const altoPieza = altoCelda < CELDA_COMPACTA ? 22 : 32
  const disponible = altoCelda - CABEZA_CELDA
  const entran = Math.floor(
    (disponible + SEPARACION_PIEZAS) / (altoPieza + SEPARACION_PIEZAS),
  )
  return Math.max(1, Math.min(total, entran))
}
